import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send, Eye, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/StatusBadge';
import { PanDirectoryCard } from '@/components/PanDirectoryCard';
import { OtpOverlay, type OtpEvent } from '@/components/OtpOverlay';
import { Picker, type PickerItem } from '@/components/Picker';
import { BookingHeadSelect } from '@/components/BookingHeadSelect';
import { RelayHealthStrip } from '@/components/RelayHealthStrip';
import { CurrencySelect } from '@/components/ui/currency-select';
import { WalletSelect } from '@/components/ui/wallet-select';
import { api, ApiError } from '@/lib/api';
import { setOtpBadge, clearOtpBadge } from '@/lib/notifications';
import { useAuth } from '@/contexts/AuthContext';
import { useSse } from '@/hooks/useSse';
import { formatINR, formatTimeAgo } from '@/lib/utils';

export interface DashboardProps {
  onSseStatus: (s: ReturnType<typeof useSse>['status']) => void;
}

interface CardItem {
  id: string;
  nickname: string;
  bank: string;
  lastFour: string;
  cardholderName: string;
  active: boolean;
}

interface BankAccountItem {
  id: string;
  nickname: string;
  bank: string;
  accountNumberMasked?: string | null;
  active: boolean;
}

interface WalletItem {
  id: string;
  name: string;
  vendor: string;
  active: boolean;
  loadingModel?: 'PREPAID' | 'PER_BOOKING';
  senderPattern?: string | null;
  otpDeviceId?: string | null;
  otpOwnerUserId?: string | null;
  portalUsername?: string | null;
  hasPortalPassword?: boolean;
  portalPasswordUpdatedAt?: string | null;
}

interface CardRequestItem {
  id: string;
  cardId: string;
  vendor: string;
  vendorId?: string | null;
  clientName: string | null;
  clientId?: string | null;
  tripId?: string | null;
  /** Joined from `trips.name` on the server — needed to pre-fill the
   *  Trip picker on a Repeat-this-request click; the picker takes a
   *  {id, label}, not just an id. */
  tripName?: string | null;
  paymentNature?: string | null;
  amount: string | null;
  currency: string | null;
  status: string;
  createdAt: string;
  approvedAt: string | null;
  isBeneficiaryAddition: boolean;
  bookingHead?: string | null;
  cardLabel?: string | null;
  cardOwnerName?: string | null;
  // Delivered OTP, joined server-side — survives a reload, unlike the
  // in-memory live-SSE copy.
  otpSmsBody?: string | null;
  otpSenderId?: string | null;
  otpReceivedAt?: string | null;
}

/**
 * "Repeat this request" — clicking the ↻ button on a past request in
 * the timeline dispatches this event, which the active CardRequestForm
 * listens for and uses to pre-fill its fields. A custom event avoids
 * threading an onRepeat callback through three layers of components
 * (DashboardPage → RequestForms → CardRequestForm) and through the
 * timeline branch (RequestTimeline → TimelineRow).
 */
const REPEAT_REQUEST_EVENT = 'otp-shield:repeat-card-request';
interface RepeatRequestDetail {
  req: CardRequestItem;
}

interface BankTransferRequestItem {
  id: string;
  bankAccountId: string;
  beneficiaryName: string;
  vendorId?: string | null;
  clientName: string | null;
  clientId?: string | null;
  amount: string;
  status: string;
  createdAt: string;
  approvedAt: string | null;
  bookingHead?: string | null;
  purpose?: string | null;
  isNewBeneficiary: boolean;
  requesterUserId: string;
  otpSmsBody?: string | null;
  otpSenderId?: string | null;
  otpReceivedAt?: string | null;
}

interface WalletSessionItem {
  id: string;
  walletId: string | null;
  walletName: string | null;
  walletVendor: string | null;
  assigneeUserId: string;
  status: string;
  startedAt: string;
  expiresAt: string;
  otpCount?: number;
  otpSmsBody?: string | null;
  otpSenderId?: string | null;
  otpDeliveredAt?: string | null;
}

export function DashboardPage({ onSseStatus }: DashboardProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Visa-team operators don't have netbanking access (cards only); everyone
  // else (ADMIN, ACCOUNTANT, regular OPERATORs) can raise a bank-transfer OTP.
  const canRaiseBankTransfer = user?.role === 'ADMIN'
    || user?.role === 'ACCOUNTANT'
    || (user?.role === 'OPERATOR' && user?.team !== 'VISA');
  const [otpEvent, setOtpEvent] = useState<OtpEvent | null>(null);
  // OTP log ids the user has explicitly dismissed. Once dismissed, the
  // overlay must never re-raise for that OTP — not on SSE replay after a
  // reconnect, not on tab-focus recovery. Without this the same OTP pops
  // back every time the connection cycles.
  const dismissedOtpIds = useRef<Set<string>>(new Set());
  // Right-panel timeline state: the user's last 5 raised card_requests +
  // per-request delivered-OTP cache. `recentOtps` is retained as a
  // legacy buffer used by the visibility-change backfill below; the
  // timeline itself drives off `myRequests` + `otpsByRequest`.
  const [recentOtps, setRecentOtps] = useState<OtpEvent[]>([]);
  const [otpsByRequest, setOtpsByRequest] = useState<
    Record<string, { otpCode: string; smsBody: string; senderId: string; receivedAt: string }>
  >({});

  const myRequests = useQuery<CardRequestItem[]>({
    queryKey: ['card-requests'],
    queryFn: () => api<CardRequestItem[]>('/api/card-requests'),
    refetchOnWindowFocus: true,
  });

  // Bank-transfer requests also belong in the timeline. The endpoint
  // returns all company requests for an admin, so filter to the user's
  // own below.
  const myBankTransfers = useQuery<BankTransferRequestItem[]>({
    queryKey: ['bank-transfer-requests'],
    queryFn: () => api<BankTransferRequestItem[]>('/api/bank-transfer-requests'),
    refetchOnWindowFocus: true,
    refetchInterval: 20_000,
  });

  // Active sessions — wallet OTP requests are session rows (not
  // card_requests), so the timeline pulls them from here too.
  const activeSessions = useQuery<WalletSessionItem[]>({
    queryKey: ['active-sessions'],
    queryFn: () => api<WalletSessionItem[]>('/api/sessions/active'),
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });

  const cards = useQuery<CardItem[]>({ queryKey: ['cards'], queryFn: () => api('/api/cards') });
  const bankAccounts = useQuery<BankAccountItem[]>({
    queryKey: ['bank-accounts'],
    queryFn: () => api('/api/bank-accounts'),
  });
  const wallets = useQuery<WalletItem[]>({
    queryKey: ['wallets'],
    queryFn: () => api('/api/wallets'),
  });

  const cardById = useMemo(() => {
    const m = new Map<string, CardItem>();
    cards.data?.forEach((c) => m.set(c.id, c));
    return m;
  }, [cards.data]);

  // Backfill OTPs routed to this user. Runs on mount AND every time the
  // tab regains focus, so an OTP that fired while the tab was minimized
  // surfaces the moment the user comes back. If the most-recent OTP is
  // newer than what's already showing, the overlay re-fires so the user
  // sees the live "OTP arrived" pop instead of having to scroll.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    let lastSeenLogId: string | null = null;

    const refetch = async (raiseOverlay: boolean) => {
      try {
        const since = new Date(Date.now() - 30 * 60 * 1000).toISOString();
        const r = await api<{ items: Array<{ id: string; senderId: string; smsBody: string; routingAction: string | null; cardId: string | null; createdAt: string; destinationUserId: string | null }> }>(
          `/api/otp-logs?routingAction=AUTO_FORWARD&limit=20`,
        );
        if (cancelled) return;
        const mineRecent = r.items.filter(
          (x) =>
            x.destinationUserId === user.id &&
            new Date(x.createdAt).toISOString() >= since,
        );
        const seeded: OtpEvent[] = mineRecent.map((x) => {
          const otpCode = (x.smsBody.match(/\b\d{4,8}\b/) ?? [''])[0]!;
          return {
            otpLogId: x.id,
            otpCode,
            smsBody: x.smsBody,
            senderId: x.senderId,
            action: x.routingAction ?? 'AUTO_FORWARD',
            cardId: x.cardId ?? undefined,
            receivedAt: x.createdAt,
            cardNickname: x.cardId ? cardById.get(x.cardId)?.nickname : undefined,
          };
        });
        setRecentOtps((prev) => {
          const seen = new Set(prev.map((p) => p.otpLogId));
          const merged = [...seeded, ...prev.filter((p) => !seeded.find((s) => s.otpLogId === p.otpLogId))];
          return merged.slice(0, 10);
        });
        // If the most-recent OTP is new since last refetch, raise the
        // big overlay — that's the "switch back to tab and OTP appears"
        // experience the user asked for.
        if (
          raiseOverlay &&
          seeded[0] &&
          seeded[0].otpLogId !== lastSeenLogId &&
          !dismissedOtpIds.current.has(seeded[0].otpLogId)
        ) {
          setOtpEvent(seeded[0]);
        }
        if (seeded[0]) lastSeenLogId = seeded[0].otpLogId;
      } catch {
        // best-effort — SSE will catch the live ones
      }
    };

    void refetch(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, cardById.size]);

  const sse = useSse({
    onEvent: (ev) => {
      // OTP delivery → big overlay + add to recent list, and if the
      // engine bound it to a card_request, surface inline on the
      // matching row in the right-panel timeline.
      if (ev.type === 'otp_ready') {
        const data = ev.data as OtpEvent & { cardRequestId?: string | null };
        const enriched: OtpEvent = {
          ...data,
          cardNickname: data.cardId ? cardById.get(data.cardId)?.nickname : undefined,
        };
        // Skip the overlay for an OTP the user already dismissed — an
        // SSE reconnect can replay the same otp_ready event.
        if (!enriched.otpLogId || !dismissedOtpIds.current.has(enriched.otpLogId)) {
          setOtpEvent(enriched);
        }
        setRecentOtps((prev) => [enriched, ...prev].slice(0, 10));
        if (data.cardRequestId && data.otpCode) {
          setOtpsByRequest((prev) => ({
            ...prev,
            [data.cardRequestId!]: {
              otpCode: data.otpCode,
              smsBody: data.smsBody ?? '',
              senderId: data.senderId ?? '',
              receivedAt: data.receivedAt ?? new Date().toISOString(),
            },
          }));
          qc.invalidateQueries({ queryKey: ['card-requests'] });
        }
        // An OTP delivery can complete a wallet session, a card request
        // or a bank-transfer request — refresh all three so the timeline
        // flips to "delivered / completed" without waiting for a poll.
        qc.invalidateQueries({ queryKey: ['active-sessions'] });
        qc.invalidateQueries({ queryKey: ['card-requests'] });
        qc.invalidateQueries({ queryKey: ['bank-transfer-requests'] });
      }
      // Live request lifecycle → invalidate
      if (
        ev.type === 'request_pending' ||
        ev.type === 'request_approved' ||
        ev.type === 'request_declined' ||
        ev.type === 'request_timed_out'
      ) {
        qc.invalidateQueries({ queryKey: ['card-requests'] });
        qc.invalidateQueries({ queryKey: ['bank-transfer-requests'] });
      }
      // Session lifecycle → refresh the right-pane timeline so a raised
      // wallet session shows ARMED immediately and flips to delivered /
      // expired without needing a manual refresh.
      if (
        ev.type === 'session_activated' ||
        ev.type === 'session_expired' ||
        ev.type === 'wallet_freed'
      ) {
        qc.invalidateQueries({ queryKey: ['active-sessions'] });
      }
    },
  });

  useEffect(() => {
    onSseStatus(sse.status);
  }, [sse.status, onSseStatus]);

  // Tab-title + app-icon badge while an OTP is waiting to be handled —
  // visible even when the dashboard tab is in the background, where the
  // OS notification toast is the only other signal. Clears on dismiss.
  useEffect(() => {
    if (otpEvent) setOtpBadge();
    else clearOtpBadge();
    return () => clearOtpBadge();
  }, [otpEvent]);

  return (
    <>
      <RelayHealthStrip />
      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <RequestForms
            cards={cards.data ?? []}
            bankAccounts={bankAccounts.data ?? []}
            wallets={(wallets.data ?? []).filter((w) => w.active && !!w.senderPattern)}
            canRaiseBankTransfer={canRaiseBankTransfer}
          />
          <LogNoOtpBookingCard cards={cards.data ?? []} bankAccounts={bankAccounts.data ?? []} />
          <WalletBookingForm
            wallets={(wallets.data ?? []).filter((w) => w.active)}
            cards={cards.data ?? []}
          />
          {/* "Pay a credit card" tile hidden until the CC limit / used
              signal quality improves — a bunch of cards were showing
              unreliable numbers off the SMS snapshots. Re-enable when
              the data model is tightened. */}
          {false && <PayCreditCardCard bankAccounts={bankAccounts.data ?? []} />}
          <LogCancellationCard />
        </div>

        <aside className="space-y-6">
          <RequestTimeline
            requests={(myRequests.data ?? []).slice(0, 50)}
            bankRequests={(myBankTransfers.data ?? [])
              .filter((r) => r.requesterUserId === user?.id)
              .slice(0, 50)}
            walletSessions={(activeSessions.data ?? [])
              .filter((s) => s.walletId && s.assigneeUserId === user?.id)
              .slice(0, 50)}
            otpsByRequest={otpsByRequest}
          />
          <PanDirectoryCard />
        </aside>
      </div>

      {otpEvent && (
        <OtpOverlay
          event={otpEvent}
          onDismiss={() => {
            if (otpEvent.otpLogId) dismissedOtpIds.current.add(otpEvent.otpLogId);
            setOtpEvent(null);
          }}
          showRoutingPath={user?.role === 'ADMIN'}
        />
      )}

    </>
  );
}

/** Log a booking paid from a B2B wallet — wallet, head, client, trip,
 *  amount. Posts a wallet spend so wallet usage is tracked alongside
 *  card / bank bookings. */
function WalletBookingForm({ wallets, cards }: { wallets: WalletItem[]; cards: CardItem[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Wallets</CardTitle>
        <CardDescription>
          Record a booking paid from a B2B wallet, or load a wallet via a card OTP.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="log">
          <TabsList>
            <TabsTrigger value="log">Log a wallet booking</TabsTrigger>
            <TabsTrigger value="load">Load wallet</TabsTrigger>
          </TabsList>
          <TabsContent value="log">
            <WalletLogBookingForm wallets={wallets} cards={cards} />
          </TabsContent>
          <TabsContent value="load">
            <WalletLoadForm wallets={wallets} cards={cards} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function WalletLogBookingForm({ wallets, cards }: { wallets: WalletItem[]; cards: CardItem[] }) {
  const qc = useQueryClient();
  const [walletId, setWalletId] = useState('');
  const [head, setHead] = useState<BookingHead | ''>('');
  const [client, setClient] = useState<{ id: string; label: string } | null>(null);
  const [trip, setTrip] = useState<{ id: string; label: string } | null>(null);
  const [amount, setAmount] = useState('');
  const [paidVia, setPaidVia] = useState<'WALLET' | 'CARD' | 'BANK' | 'UPI'>('WALLET');
  const [paidCardId, setPaidCardId] = useState('');
  const [paidBankAccountId, setPaidBankAccountId] = useState('');
  const [paidUpiVpa, setPaidUpiVpa] = useState('');
  const banks = useQuery<{ id: string; nickname: string }[]>({
    queryKey: ['bank-accounts-select'],
    queryFn: () => api('/api/bank-accounts'),
  });
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const searchClients = async (q: string): Promise<PickerItem[]> => {
    const params = new URLSearchParams({ limit: '20' });
    if (q) params.set('q', q);
    const r = await api<{ id: string; name: string; status: string }[]>(`/api/clients?${params}`);
    return r.map((c) => ({ id: c.id, label: c.name, sub: c.status.toLowerCase() }));
  };
  const createClient = async (name: string): Promise<PickerItem> => {
    const c = await api<{ id: string; name: string }>('/api/clients', {
      method: 'POST',
      body: { name, status: 'LEAD' },
    });
    return { id: c.id, label: c.name };
  };
  const searchTrips = async (q: string): Promise<PickerItem[]> => {
    if (!client) return [];
    const params = new URLSearchParams({ clientId: client.id, limit: '20' });
    if (q) params.set('q', q);
    const r = await api<{ id: string; name: string; destination: string | null }[]>(
      `/api/trips?${params}`,
    );
    return r.map((t) => ({ id: t.id, label: t.name, sub: t.destination ?? undefined }));
  };
  const createTrip = async (name: string): Promise<PickerItem | null> => {
    if (!client) return null;
    const t = await api<{ id: string; name: string }>('/api/trips', {
      method: 'POST',
      body: { clientId: client.id, name, status: 'PLANNING' },
    });
    return { id: t.id, label: t.name };
  };

  const paidReady =
    paidVia === 'WALLET' ||
    (paidVia === 'CARD' && !!paidCardId) ||
    (paidVia === 'BANK' && !!paidBankAccountId) ||
    (paidVia === 'UPI' && /^[\w.+-]+@[\w.-]+$/.test(paidUpiVpa));

  const ready =
    !!walletId && !!head && !!client && !!trip && /^\d+(\.\d{1,2})?$/.test(amount) && paidReady;

  const submit = useMutation({
    mutationFn: async () =>
      api(`/api/wallets/${walletId}/spends`, {
        method: 'POST',
        body: {
          amount,
          clientId: client!.id,
          tripId: trip!.id,
          bookingHead: head || undefined,
          occurredAt: new Date().toISOString(),
          paidVia,
          paidCardId: paidVia === 'CARD' ? paidCardId : undefined,
          paidBankAccountId: paidVia === 'BANK' ? paidBankAccountId : undefined,
          paidUpiVpa: paidVia === 'UPI' ? paidUpiVpa : undefined,
        },
      }),
    onSuccess: () => {
      setOkMsg(
        paidVia === 'WALLET'
          ? 'Wallet booking logged.'
          : `Booking logged (paid via ${paidVia.toLowerCase()}). A pending debit is now tracking.`,
      );
      setError(null);
      setWalletId('');
      setHead('');
      setClient(null);
      setTrip(null);
      setAmount('');
      setPaidVia('WALLET');
      setPaidCardId('');
      setPaidBankAccountId('');
      setPaidUpiVpa('');
      qc.invalidateQueries({ queryKey: ['wallets'] });
    },
    onError: (e) => {
      setError((e as ApiError).message || 'Could not log the booking.');
      setOkMsg(null);
    },
  });

  return (
    <>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Wallet used</Label>
            <WalletSelect
              wallets={wallets.map((w) => ({ id: w.id, name: w.name, vendor: w.vendor, active: w.active, loadingModel: w.loadingModel }))}
              value={walletId}
              onChange={setWalletId}
            />
          </div>
          <div className="space-y-2">
            <Label>Booking head</Label>
            <BookingHeadSelect
              value={head}
              onChange={(v) => setHead(v as BookingHead)}
              category="CLIENT_BOOKING"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Client / lead</Label>
            <Picker
              value={client}
              onChange={(v) => {
                setClient(v);
                setTrip(null);
              }}
              search={searchClients}
              onCreateNew={createClient}
              placeholder="Search clients — or type a new name"
            />
          </div>
          <div className="space-y-2">
            <Label>Trip</Label>
            <Picker
              value={trip}
              onChange={setTrip}
              search={searchTrips}
              onCreateNew={createTrip}
              placeholder={client ? 'Search or add a trip' : 'Pick a client first'}
              disabled={!client}
              refetchKey={client?.id}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wallet-booking-amount">Amount (INR)</Label>
            <Input
              id="wallet-booking-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="e.g. 24500"
            />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <Label>Paid via</Label>
          <div className="inline-flex rounded-md border p-0.5">
            {(['WALLET', 'CARD', 'BANK', 'UPI'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setPaidVia(opt)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition ${
                  paidVia === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.charAt(0) + opt.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
          {paidVia === 'CARD' ? (
            <select
              value={paidCardId}
              onChange={(e) => setPaidCardId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select card —</option>
              {cards.map((c) => (
                <option key={c.id} value={c.id}>{c.nickname} · ••{c.lastFour}</option>
              ))}
            </select>
          ) : null}
          {paidVia === 'BANK' ? (
            <select
              value={paidBankAccountId}
              onChange={(e) => setPaidBankAccountId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select bank account —</option>
              {(banks.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>{b.nickname}</option>
              ))}
            </select>
          ) : null}
          {paidVia === 'UPI' ? (
            <Input
              value={paidUpiVpa}
              onChange={(e) => setPaidUpiVpa(e.target.value)}
              placeholder="beneficiary@upi"
            />
          ) : null}
        </div>
        {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
        {okMsg ? <p className="mt-2 text-sm text-emerald-700">{okMsg}</p> : null}
        <div className="mt-3">
          <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submit.isPending ? 'Logging…' : 'Log wallet booking'}
          </Button>
        </div>
    </>
  );
}

/**
 * Load a B2B wallet via a card OTP. Operator picks the wallet to top up,
 * the card to fund it from, and the amount; we raise a card_request
 * behind the scenes so the bank OTP routes through the normal admin
 * approval + back-route plumbing. bookingHead is fixed to WALLET_LOAD,
 * payment nature is OPERATING_EXPENSE, vendor = the wallet's name.
 */
type CcSummary = {
  id: string;
  nickname: string;
  bank: string;
  lastFour: string;
  creditLimit: string | null;
  dueDay: number | null;
  used: string;
  available: string | null;
  daysToDue: number | null;
  hasBeneficiary: boolean;
  lastPaymentAt: string | null;
  lastPaymentAmount: string | null;
};

type NoOtpVendor = {
  id: string;
  name: string;
  shortName: string | null;
  websiteUrl: string | null;
  vendorKind: 'MERCHANT' | 'INDIVIDUAL_PAYOUT_PLATFORM';
};

type RecentPayee = { payee: string; lastAt: string | null; uses: number };

function LogNoOtpBookingCard({ cards }: { cards: CardItem[]; bankAccounts: BankAccountItem[] }) {
  const qc = useQueryClient();
  const [vendorId, setVendorId] = useState('');
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [paidTo, setPaidTo] = useState('');
  const [nature, setNature] = useState<'CLIENT_BOOKING' | 'OPERATING_EXPENSE'>('CLIENT_BOOKING');
  const [client, setClient] = useState<{ id: string; label: string } | null>(null);
  const [trip, setTrip] = useState<{ id: string; label: string } | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [cardId, setCardId] = useState('');
  const [head, setHead] = useState<BookingHead | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const noOtpVendors = useQuery<NoOtpVendor[]>({
    queryKey: ['no-otp-vendors'],
    queryFn: () => api('/api/vendors?noOtpOnly=true&limit=200'),
  });

  const selectedVendor = (noOtpVendors.data ?? []).find((v) => v.id === vendorId);
  const isPayoutPlatform = selectedVendor?.vendorKind === 'INDIVIDUAL_PAYOUT_PLATFORM';

  // Recent payees for the payout-platform autosuggest. Only queried
  // when the selected vendor is a payout platform.
  const recentPayees = useQuery<RecentPayee[]>({
    queryKey: ['recent-payees', vendorId],
    queryFn: () => api(`/api/vendors/${vendorId}/recent-payees`),
    enabled: !!vendorId && isPayoutPlatform,
  });

  // Nature auto-flips to OPERATING_EXPENSE when payout platform is picked
  // (paying a driver via PayPal is almost always an office expense) and
  // back to CLIENT_BOOKING when a merchant vendor is picked. Ops can
  // still override the toggle if they need to.
  useEffect(() => {
    if (isPayoutPlatform) setNature('OPERATING_EXPENSE');
    else setNature('CLIENT_BOOKING');
    setPaidTo('');
  }, [isPayoutPlatform]);

  const searchClients = async (q: string): Promise<PickerItem[]> => {
    const params = new URLSearchParams({ limit: '20' });
    if (q) params.set('q', q);
    const r = await api<{ id: string; name: string; status: string }[]>(`/api/clients?${params}`);
    return r.map((c) => ({ id: c.id, label: c.name, sub: c.status.toLowerCase() }));
  };
  const createClient = async (name: string): Promise<PickerItem> => {
    const c = await api<{ id: string; name: string }>('/api/clients', {
      method: 'POST',
      body: { name, status: 'LEAD' },
    });
    return { id: c.id, label: c.name };
  };
  const searchTrips = async (q: string): Promise<PickerItem[]> => {
    if (!client) return [];
    const params = new URLSearchParams({ clientId: client.id, limit: '20' });
    if (q) params.set('q', q);
    const r = await api<{ id: string; name: string; destination: string | null }[]>(
      `/api/trips?${params}`,
    );
    return r.map((t) => ({ id: t.id, label: t.name, sub: t.destination ?? undefined }));
  };
  const createTrip = async (name: string): Promise<PickerItem | null> => {
    if (!client) return null;
    const t = await api<{ id: string; name: string }>('/api/trips', {
      method: 'POST',
      body: { clientId: client.id, name, status: 'PLANNING' },
    });
    return { id: t.id, label: t.name };
  };

  const isOfficeExpense = nature === 'OPERATING_EXPENSE';

  // Amount normalisation — strip commas / spaces / stray chars so
  // "19,900" or "19 900" pass validation. The onChange also enforces
  // this on input; kept here as defense-in-depth for paste-inject.
  const cleanAmount = amount.replace(/[^0-9.]/g, '');

  const ready =
    !!vendorId &&
    !!cardId &&
    /^\d+(\.\d{1,2})?$/.test(cleanAmount) &&
    currency.length === 3 &&
    (isPayoutPlatform ? !!paidTo.trim() : true) &&
    (isOfficeExpense ? true : !!client && !!trip);

  const submit = useMutation({
    mutationFn: () =>
      api('/api/transactions/log-no-otp-booking', {
        method: 'POST',
        body: {
          cardId,
          vendorId,
          paidTo: isPayoutPlatform ? paidTo.trim() : undefined,
          paymentNature: nature,
          clientId: isOfficeExpense ? undefined : client!.id,
          tripId: isOfficeExpense ? undefined : trip!.id,
          amount: cleanAmount,
          currency,
          bookingHead: head || undefined,
          occurredAt: new Date().toISOString(),
        },
      }),
    onSuccess: () => {
      setOkMsg(
        isPayoutPlatform
          ? `Payment logged to ${paidTo.trim()}. The debit SMS will auto-attribute.`
          : 'Booking logged. The debit SMS will auto-attribute to it.',
      );
      setError(null);
      setVendorId('');
      setPaidTo('');
      setClient(null);
      setTrip(null);
      setAmount('');
      setCurrency('INR');
      setCardId('');
      setHead('');
      qc.invalidateQueries({ queryKey: ['debits-ledger'] });
    },
    onError: (e) => {
      setError((e as ApiError).message || 'Could not log the booking.');
      setOkMsg(null);
    },
  });

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Log a booking (no OTP)</CardTitle>
        <CardDescription>
          Bookings on platforms that auto-charge a card without an OTP — Expedia, Trip.com, PayPal,
          Booking.com international, etc. Bank transfers always need an OTP, so this is card-only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Vendor / merchant</Label>
              <button
                type="button"
                onClick={() => setShowAddVendor(true)}
                className="text-xs text-primary hover:underline"
              >
                + Add new vendor
              </button>
            </div>
            <select
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select vendor —</option>
              {(noOtpVendors.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.shortName ? `${v.shortName} · ${v.name}` : v.name}
                  {v.vendorKind === 'INDIVIDUAL_PAYOUT_PLATFORM' ? '  · payout platform' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Payout-platform-only fields. PayPal/Wise etc. bill an
              individual, so we capture the payee here and default
              nature to office expense. */}
          {isPayoutPlatform ? (
            <>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="paidTo">Paid to (individual / freelancer / driver)</Label>
                <Input
                  id="paidTo"
                  value={paidTo}
                  onChange={(e) => setPaidTo(e.target.value)}
                  placeholder="e.g. Ravi Kumar · Uber driver Bali"
                  list="recent-payees-datalist"
                />
                <datalist id="recent-payees-datalist">
                  {(recentPayees.data ?? []).map((p) => (
                    <option key={p.payee} value={p.payee}>
                      {p.uses} time{p.uses > 1 ? 's' : ''}
                      {p.lastAt ? ` · last ${new Date(p.lastAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}
                    </option>
                  ))}
                </datalist>
                {(recentPayees.data ?? []).length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Recent:{' '}
                    {recentPayees.data!.slice(0, 4).map((p, i) => (
                      <span key={p.payee}>
                        {i > 0 ? ' · ' : ''}
                        <button
                          type="button"
                          onClick={() => setPaidTo(p.payee)}
                          className="text-primary hover:underline"
                        >
                          {p.payee}
                        </button>
                      </span>
                    ))}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Nature</Label>
                <div className="inline-flex rounded-md border p-0.5">
                  {(
                    [
                      ['CLIENT_BOOKING', 'Client booking'],
                      ['OPERATING_EXPENSE', 'Office / operating expense'],
                    ] as const
                  ).map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setNature(val)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm transition ${
                        nature === val ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {/* Client + Trip appear only for client bookings (default for
              merchants; opt-in for payout-platform payments). */}
          {!isOfficeExpense ? (
            <>
              <div className="space-y-2">
                <Label>Client / lead</Label>
                <Picker
                  value={client}
                  onChange={(v) => {
                    setClient(v);
                    setTrip(null);
                  }}
                  search={searchClients}
                  onCreateNew={createClient}
                  placeholder="Search clients — or type a new name"
                />
              </div>
              <div className="space-y-2">
                <Label>Trip</Label>
                <Picker
                  value={trip}
                  onChange={setTrip}
                  search={searchTrips}
                  onCreateNew={createTrip}
                  placeholder={client ? 'Search or add a trip' : 'Pick a client first'}
                  disabled={!client}
                  refetchKey={client?.id}
                />
              </div>
            </>
          ) : null}

          <div className="space-y-2">
            <Label>{isOfficeExpense ? 'Expense head' : 'Booking head'}</Label>
            <BookingHeadSelect
              value={head}
              onChange={(v) => setHead(v as BookingHead)}
              category={isOfficeExpense ? 'OPERATING_EXPENSE' : 'CLIENT_BOOKING'}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Amount</Label>
            <div className="flex gap-2">
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="e.g. 189.50"
                className="flex-1"
              />
              <div className="w-32 shrink-0">
                <CurrencySelect value={currency} onChange={setCurrency} />
              </div>
            </div>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Charged to card</Label>
            <select
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select card —</option>
              {cards.filter((c) => c.active).map((c) => (
                <option key={c.id} value={c.id}>{c.nickname} · ••{c.lastFour}</option>
              ))}
            </select>
          </div>
        </div>
        {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
        {okMsg ? <p className="mt-2 text-sm text-emerald-700">{okMsg}</p> : null}
        <div className="mt-3">
          <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submit.isPending ? 'Logging…' : (isPayoutPlatform ? 'Log payment' : 'Log booking')}
          </Button>
        </div>
      </CardContent>
    </Card>
    {showAddVendor ? (
      <AddNoOtpVendorDialog
        onClose={() => setShowAddVendor(false)}
        onCreated={(v) => {
          qc.invalidateQueries({ queryKey: ['no-otp-vendors'] });
          setVendorId(v.id);
          setShowAddVendor(false);
        }}
      />
    ) : null}
    </>
  );
}

function AddNoOtpVendorDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (v: NoOtpVendor) => void;
}) {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [vendorKind, setVendorKind] = useState<'MERCHANT' | 'INDIVIDUAL_PAYOUT_PLATFORM'>('MERCHANT');
  const [error, setError] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: (): Promise<NoOtpVendor> =>
      api('/api/vendors', {
        method: 'POST',
        body: {
          name: name.trim(),
          shortName: shortName.trim() || undefined,
          websiteUrl: websiteUrl.trim() || undefined,
          category: 'OTA',
          isNoOtp: true,
          vendorKind,
        },
      }),
    onSuccess: onCreated,
    onError: (e) => setError((e as ApiError).message || 'Could not add vendor.'),
  });
  const urlValid =
    !websiteUrl.trim() || /^https?:\/\/[^\s]+/i.test(websiteUrl.trim());
  const ready = !!name.trim() && urlValid;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-1">Add a no-OTP vendor</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Platforms that auto-charge a card without an OTP. Ops will pick this in the "Log a booking (no OTP)" flow.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Booking.com" />
          </div>
          <div className="space-y-1">
            <Label>Short name (what the office calls it)</Label>
            <Input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="Booking" />
          </div>
          <div className="space-y-1">
            <Label>Website URL</Label>
            <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://www.booking.com" />
            {!urlValid ? <p className="text-xs text-destructive">Must start with http:// or https://</p> : null}
          </div>
          <div className="space-y-2">
            <Label>Kind of vendor</Label>
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={vendorKind === 'MERCHANT'}
                  onChange={() => setVendorKind('MERCHANT')}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm">Merchant / booking site</div>
                  <div className="text-xs text-muted-foreground">Ryanair, Hilton, Trip.com — the vendor IS the merchant.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={vendorKind === 'INDIVIDUAL_PAYOUT_PLATFORM'}
                  onChange={() => setVendorKind('INDIVIDUAL_PAYOUT_PLATFORM')}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm">Payout platform for individuals</div>
                  <div className="text-xs text-muted-foreground">PayPal, Wise, Payoneer — used to pay drivers / freelancers directly.</div>
                </div>
              </label>
            </div>
          </div>
        </div>
        {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Add vendor
          </Button>
        </div>
      </div>
    </div>
  );
}

type ParentDebit = {
  id: string;
  amount: string;
  currency: string;
  amountFx: string | null;
  currencyFx: string | null;
  vendor: string | null;
  clientName: string | null;
  clientDisplayName: string | null;
  createdAt: string;
  paymentMethod: string;
};

const CURRENCY_SYMBOL: Record<string, string> = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$',
  CAD: 'C$', SGD: 'S$', HKD: 'HK$', CHF: 'CHF ', CNY: '¥', THB: '฿',
  MYR: 'RM', KRW: '₩', IDR: 'Rp', VND: '₫', AED: 'د.إ ', ZAR: 'R',
  NZD: 'NZ$',
};

function currencyPrefix(code: string | null | undefined): string {
  if (!code) return '';
  return CURRENCY_SYMBOL[code.toUpperCase()] ?? `${code.toUpperCase()} `;
}

// Prefer the originally-billed amount + currency when the debit was
// FX-matched (booking was in EUR, INR debit landed via card FX). The
// FX-side is what ops thinks in and what the vendor will refund in.
function parentDebitDisplay(d: ParentDebit): { amount: number; currency: string; label: string } {
  const useFx = !!d.amountFx && !!d.currencyFx && d.currencyFx !== d.currency;
  const currency = useFx ? d.currencyFx! : d.currency;
  const amount = parseFloat((useFx ? d.amountFx! : d.amount) || '0');
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return {
    amount,
    currency,
    label: `${currencyPrefix(currency)}${amount.toLocaleString(locale, { maximumFractionDigits: 2 })}`,
  };
}

function LogCancellationCard() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [amountFilter, setAmountFilter] = useState('');
  const [parent, setParent] = useState<ParentDebit | null>(null);
  const [expectedAmount, setExpectedAmount] = useState('');
  const [expectedCurrency, setExpectedCurrency] = useState('INR');
  const [instrument, setInstrument] = useState<'CARD' | 'BANK' | 'WALLET'>('CARD');
  const [refundCardId, setRefundCardId] = useState('');
  const [refundBankAccountId, setRefundBankAccountId] = useState('');
  const [refundWalletId, setRefundWalletId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const results = useQuery<ParentDebit[]>({
    queryKey: ['refund-parent-search', q, amountFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (amountFilter.trim()) params.set('amount', amountFilter.trim());
      params.set('limit', '15');
      return api(`/api/refund-notes/search-parent?${params.toString()}`);
    },
    enabled: !!q.trim() || !!amountFilter.trim(),
  });
  const cardsQ = useQuery<CardItem[]>({ queryKey: ['cards'], queryFn: () => api('/api/cards') });
  const banksQ = useQuery<{ id: string; nickname: string }[]>({
    queryKey: ['bank-accounts-select'],
    queryFn: () => api('/api/bank-accounts'),
  });
  const walletsQ = useQuery<{ id: string; name: string }[]>({
    queryKey: ['wallets'],
    queryFn: () => api('/api/wallets'),
  });

  const submit = useMutation({
    mutationFn: () =>
      api('/api/refund-notes', {
        method: 'POST',
        body: {
          transactionId: parent!.id,
          expectedAmount,
          currency: expectedCurrency,
          reason: reason || undefined,
          refundCardId: instrument === 'CARD' ? refundCardId : undefined,
          refundBankAccountId: instrument === 'BANK' ? refundBankAccountId : undefined,
          refundWalletId: instrument === 'WALLET' ? refundWalletId : undefined,
        },
      }),
    onSuccess: () => {
      setOkMsg('Cancellation logged. Accounts will confirm when the credit lands.');
      setError(null);
      setParent(null);
      setExpectedAmount('');
      setExpectedCurrency('INR');
      setRefundCardId('');
      setRefundBankAccountId('');
      setRefundWalletId('');
      setReason('');
      setQ('');
      setAmountFilter('');
      qc.invalidateQueries({ queryKey: ['refund-notes'] });
    },
    onError: (e) => setError((e as ApiError).message || 'Could not raise refund note.'),
  });

  const instrumentValid =
    (instrument === 'CARD' && !!refundCardId) ||
    (instrument === 'BANK' && !!refundBankAccountId) ||
    (instrument === 'WALLET' && !!refundWalletId);
  const ready = !!parent && /^\d+(\.\d{1,2})?$/.test(expectedAmount) && instrumentValid;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Log a cancellation / refund note</CardTitle>
        <CardDescription>
          Client cancelled a booking? Find the parent debit, note the expected refund and where it should land.
          Accounts confirms with the actual amount when the credit arrives.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!parent ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-2 sm:col-span-2">
                <Label>Search by client / vendor</Label>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Client name, vendor …"
                />
              </div>
              <div className="space-y-2">
                <Label>Or by amount (INR)</Label>
                <Input
                  inputMode="decimal"
                  value={amountFilter}
                  onChange={(e) => setAmountFilter(e.target.value)}
                  placeholder="e.g. 42000"
                />
              </div>
            </div>
            {results.data && results.data.length > 0 ? (
              <div className="border rounded-md divide-y max-h-60 overflow-auto">
                {results.data.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setParent(r);
                      const disp = parentDebitDisplay(r);
                      setExpectedAmount(disp.amount.toFixed(2));
                      setExpectedCurrency(disp.currency);
                    }}
                    className="w-full flex items-center justify-between p-2 hover:bg-muted text-left"
                  >
                    <div>
                      <div className="text-sm font-medium">
                        {parentDebitDisplay(r).label}
                        {r.vendor ? <span className="text-muted-foreground"> · {r.vendor}</span> : null}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {r.clientDisplayName ?? r.clientName ?? '—'} ·{' '}
                        {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">{r.paymentMethod}</div>
                  </button>
                ))}
              </div>
            ) : (q.trim() || amountFilter.trim()) && !results.isLoading ? (
              <p className="text-sm text-muted-foreground">No matching debits.</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Type any part of the client or vendor name, or the debit amount.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border p-3 flex items-start justify-between">
              <div>
                <div className="text-sm font-medium">
                  {parentDebitDisplay(parent).label}
                  {parent.vendor ? <span className="text-muted-foreground"> · {parent.vendor}</span> : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {parent.clientDisplayName ?? parent.clientName ?? '—'} ·{' '}
                  {new Date(parent.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setParent(null)}
                className="text-xs text-muted-foreground hover:underline"
              >
                Change
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Expected refund amount</Label>
                <div className="flex gap-2">
                  <Input
                    inputMode="decimal"
                    value={expectedAmount}
                    onChange={(e) => setExpectedAmount(e.target.value)}
                    className="flex-1"
                  />
                  <div className="w-32 shrink-0">
                    <CurrencySelect value={expectedCurrency} onChange={setExpectedCurrency} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Defaults to the currency the client was billed in. Change if the vendor is refunding in a different currency.
                </p>
              </div>
              <div className="space-y-1">
                <Label>Refund lands on</Label>
                <div className="inline-flex rounded-md border p-0.5">
                  {(['CARD', 'BANK', 'WALLET'] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setInstrument(opt)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm ${
                        instrument === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {opt.charAt(0) + opt.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {instrument === 'CARD' ? (
              <select
                value={refundCardId}
                onChange={(e) => setRefundCardId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— Select card —</option>
                {(cardsQ.data ?? []).filter((c) => c.active).map((c) => (
                  <option key={c.id} value={c.id}>{c.nickname} · ••{c.lastFour}</option>
                ))}
              </select>
            ) : instrument === 'BANK' ? (
              <select
                value={refundBankAccountId}
                onChange={(e) => setRefundBankAccountId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— Select bank —</option>
                {(banksQ.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>{b.nickname}</option>
                ))}
              </select>
            ) : (
              <select
                value={refundWalletId}
                onChange={(e) => setRefundWalletId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— Select wallet —</option>
                {(walletsQ.data ?? []).map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            )}
            <div className="space-y-1">
              <Label htmlFor="refund-note">Note — what is this refund for?</Label>
              <textarea
                id="refund-note"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Client cancelled the Bali leg — hotel refund promised by vendor by Fri"
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y min-h-[68px]"
              />
              <p className="text-xs text-muted-foreground">
                Accounts uses this to confirm the credit when it lands. Be specific.
              </p>
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {okMsg ? <p className="text-sm text-emerald-700">{okMsg}</p> : null}
            <div>
              <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
                {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Log cancellation
              </Button>
            </div>
          </div>
        )}
        {okMsg && !parent ? <p className="mt-2 text-sm text-emerald-700">{okMsg}</p> : null}
      </CardContent>
    </Card>
  );
}

function PayCreditCardCard({ bankAccounts }: { bankAccounts: BankAccountItem[] }) {
  const summaries = useQuery<CcSummary[]>({
    queryKey: ['cards', 'credit-summaries'],
    queryFn: () => api('/api/cards/credit-summaries'),
  });
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const openCard = summaries.data?.find((s) => s.id === openCardId) ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pay a credit card</CardTitle>
        <CardDescription>
          Reduce your used balance and stay under the limit — pay a card down from any bank account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {summaries.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (summaries.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No credit cards have a limit set yet. Go to Cards → set credit limit + due date to enable this tile.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(summaries.data ?? []).map((s) => {
              const limit = s.creditLimit ? parseFloat(s.creditLimit) : 0;
              const used = parseFloat(s.used || '0');
              const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
              const dueLabel =
                s.daysToDue == null
                  ? null
                  : s.daysToDue <= 0
                    ? 'Due today'
                    : s.daysToDue === 1
                      ? 'Due tomorrow'
                      : `Due in ${s.daysToDue} days`;
              return (
                <button
                  key={s.id}
                  onClick={() => setOpenCardId(s.id)}
                  className="text-left rounded-md border p-3 hover:border-primary transition"
                >
                  <div className="flex items-baseline justify-between">
                    <div className="font-medium">{s.nickname}</div>
                    <div className="text-xs text-muted-foreground">••{s.lastFour}</div>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      Used ₹{used.toLocaleString('en-IN')} / ₹{limit.toLocaleString('en-IN')}
                    </span>
                    {dueLabel ? (
                      <span className={s.daysToDue != null && s.daysToDue <= 2 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                        {dueLabel}
                      </span>
                    ) : null}
                  </div>
                  {s.lastPaymentAt ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Last paid ₹{parseFloat(s.lastPaymentAmount ?? '0').toLocaleString('en-IN')} on{' '}
                      {new Date(s.lastPaymentAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
      {openCard ? (
        <PayCreditCardModal
          summary={openCard}
          bankAccounts={bankAccounts}
          onClose={() => setOpenCardId(null)}
        />
      ) : null}
    </Card>
  );
}

function PayCreditCardModal({
  summary,
  bankAccounts,
  onClose,
}: {
  summary: CcSummary;
  bankAccounts: BankAccountItem[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [fromBankAccountId, setFromBankAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const beneficiary = useQuery<{
    name: string;
    account: string | null;
    ifsc: string | null;
    upiVpa: string | null;
  }>({
    queryKey: ['cc-beneficiary', summary.id],
    queryFn: () => api(`/api/cards/${summary.id}/cc-beneficiary`),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const b = beneficiary.data;
      if (!b?.name || !b.account || !b.ifsc) {
        throw new Error(
          'Beneficiary details missing for this card. Admin must set them via Cards → CC settings first.',
        );
      }
      return api('/api/bank-transfer-requests', {
        method: 'POST',
        body: {
          bankAccountId: fromBankAccountId,
          transferMode: 'IMPS',
          beneficiaryName: b.name,
          beneficiaryAccount: b.account,
          beneficiaryIfsc: b.ifsc,
          amount,
          bookingHead: 'CC_PAYMENT',
          paymentNature: 'OPERATING_EXPENSE',
          ccPaymentCardId: summary.id,
          purpose: `CC bill payment — ${summary.nickname} ••${summary.lastFour}`,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards', 'credit-summaries'] });
      qc.invalidateQueries({ queryKey: ['my-bank-transfers'] });
      onClose();
    },
    onError: (e) => setError((e as ApiError).message || 'Could not raise the CC payment.'),
  });

  const ready = /^\d+(\.\d{1,2})?$/.test(amount) && !!fromBankAccountId;
  const missingBeneficiary = beneficiary.isFetched && !beneficiary.data?.name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Pay {summary.nickname}</h3>
          <p className="text-sm text-muted-foreground">
            Used ₹{parseFloat(summary.used || '0').toLocaleString('en-IN')} of ₹
            {parseFloat(summary.creditLimit || '0').toLocaleString('en-IN')}
          </p>
        </div>
        {missingBeneficiary ? (
          <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            No payee saved for this card. Ask admin to set beneficiary account + IFSC via Cards → CC settings.
          </p>
        ) : beneficiary.data ? (
          <div className="mb-3 rounded-md border p-3 text-xs">
            <div className="font-medium">Paying to</div>
            <div className="mt-1 text-muted-foreground">
              {beneficiary.data.name}
              {beneficiary.data.account ? ` · ${beneficiary.data.account}` : ''}
              {beneficiary.data.ifsc ? ` · ${beneficiary.data.ifsc}` : ''}
            </div>
          </div>
        ) : null}
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cc-pay-amount">Amount (INR)</Label>
            <Input
              id="cc-pay-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="e.g. 400000"
            />
          </div>
          <div className="space-y-1">
            <Label>From bank account</Label>
            <select
              value={fromBankAccountId}
              onChange={(e) => setFromBankAccountId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select —</option>
              {bankAccounts
                .filter((b) => b.active)
                .map((b) => (
                  <option key={b.id} value={b.id}>{b.nickname}</option>
                ))}
            </select>
          </div>
        </div>
        {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!ready || missingBeneficiary || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Raise payment
          </Button>
        </div>
      </div>
    </div>
  );
}

function WalletLoadForm({ wallets, cards }: { wallets: WalletItem[]; cards: CardItem[] }) {
  const qc = useQueryClient();
  const [walletId, setWalletId] = useState('');
  const [cardId, setCardId] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const wallet = wallets.find((w) => w.id === walletId);
  const ready = !!walletId && !!cardId && /^\d+(\.\d{1,2})?$/.test(amount);

  const submit = useMutation({
    mutationFn: async () =>
      api('/api/card-requests', {
        method: 'POST',
        body: {
          cardId,
          vendor: wallet?.name ?? 'Wallet load',
          paymentNature: 'OPERATING_EXPENSE',
          bookingHead: 'WALLET_LOAD',
          amount,
        },
      }),
    onSuccess: () => {
      setOkMsg('OTP request raised — wait for admin approval.');
      setError(null);
      setWalletId('');
      setCardId('');
      setAmount('');
      qc.invalidateQueries({ queryKey: ['card-requests'] });
    },
    onError: (e) => {
      setError((e as ApiError).message || 'Could not raise OTP.');
      setOkMsg(null);
    },
  });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-2">
        <Label>Wallet to load</Label>
        <WalletSelect
          wallets={wallets.map((w) => ({ id: w.id, name: w.name, vendor: w.vendor, active: w.active, loadingModel: w.loadingModel }))}
          value={walletId}
          onChange={setWalletId}
          mode="LOAD"
        />
      </div>
      <div className="space-y-2">
        <Label>Card to use</Label>
        <select
          value={cardId}
          onChange={(e) => setCardId(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">— Select card —</option>
          {cards
            .filter((c) => c.active)
            .map((c) => (
              <option key={c.id} value={c.id}>{c.nickname} · {c.bank} ••{c.lastFour}</option>
            ))}
        </select>
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="wallet-load-amount">Amount (INR)</Label>
        <Input
          id="wallet-load-amount"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="e.g. 100000"
        />
      </div>
      {error ? <p className="sm:col-span-2 text-sm text-destructive">{error}</p> : null}
      {okMsg ? <p className="sm:col-span-2 text-sm text-emerald-700">{okMsg}</p> : null}
      <div className="sm:col-span-2">
        <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submit.isPending ? 'Raising OTP…' : 'Raise OTP'}
        </Button>
      </div>
    </div>
  );
}

function RequestForms({
  cards,
  bankAccounts,
  wallets,
  canRaiseBankTransfer,
}: {
  cards: CardItem[];
  bankAccounts: BankAccountItem[];
  wallets: WalletItem[];
  canRaiseBankTransfer: boolean;
}) {
  const hasWallets = wallets.length > 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Raise OTP request</CardTitle>
        <CardDescription>
          {canRaiseBankTransfer
            ? 'Pre-declare what you’re about to pay — card, bank transfer, or B2B wallet. Admin approves card / bank; wallet OTPs are auto-forwarded.'
            : 'Pre-declare your card payment. Admin approves before the OTP is forwarded.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="card">
          <TabsList>
            <TabsTrigger value="card">Card payment</TabsTrigger>
            {canRaiseBankTransfer ? (
              <TabsTrigger value="bank-transfer">Bank transfer</TabsTrigger>
            ) : null}
            {hasWallets ? (
              <TabsTrigger value="wallet">Wallet</TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="card">
            <CardRequestForm cards={cards} />
          </TabsContent>
          {canRaiseBankTransfer ? (
            <TabsContent value="bank-transfer">
              <BankTransferRequestForm bankAccounts={bankAccounts} />
            </TabsContent>
          ) : null}
          {hasWallets ? (
            <TabsContent value="wallet">
              <WalletRequestForm wallets={wallets} />
            </TabsContent>
          ) : null}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function WalletRequestForm({ wallets }: { wallets: WalletItem[] }) {
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [contextLabel, setContextLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      // Pass a plain object — the api() helper JSON-stringifies the
      // body itself. Passing JSON.stringify here double-encoded it
      // into a quoted string and the server's Zod schema 400'd.
      return api<{ id: string }>('/api/sessions/wallet-claim', {
        method: 'POST',
        body: {
          walletIds: selectedIds,
          durationMinutes: 5,
          contextLabel: contextLabel.trim() || undefined,
        },
      });
    },
    onSuccess: () => {
      const count = selectedIds.length;
      setOkMsg(`OTP routing armed — ${count} wallet${count === 1 ? '' : 's'} for 5 min.`);
      setError(null);
      setSelectedIds([]);
      setContextLabel('');
      qc.invalidateQueries({ queryKey: ['active-sessions'] });
    },
    onError: (err) => {
      // 409 wallet_locked — another operator holds this shared wallet.
      // Surface who + when it frees; the server already registered this
      // user as a waiter so they'll get a "wallet free" ping.
      if (err instanceof ApiError && err.status === 409) {
        const b = err.body as
          | { error?: string; walletName?: string; holderName?: string; freesAt?: string }
          | undefined;
        if (b?.error === 'wallet_locked') {
          const secs = b.freesAt
            ? Math.max(0, Math.round((new Date(b.freesAt).getTime() - Date.now()) / 1000))
            : null;
          setError(
            `${b.walletName ?? 'That wallet'} is in use by ${b.holderName ?? 'another operator'}. ` +
              (secs != null
                ? `It frees in about ${secs}s — you'll get a notification when it does.`
                : "You'll get a notification when it's free."),
          );
          setOkMsg(null);
          return;
        }
      }
      const msg = err instanceof ApiError ? (err.message || 'Failed') : 'Failed';
      setError(msg);
      setOkMsg(null);
    },
  });

  // Claim context — wallets that already have an unclaimed OTP waiting
  // (LOCAL_ONLY, last 15 min). Lets the person raising a session see
  // "1 OTP waiting · 23s ago" before they even trigger one.
  const otpContext = useQuery<Array<{ walletId: string; unclaimedCount: number; lastOtpAt: string | null }>>({
    queryKey: ['wallet-otp-context'],
    queryFn: () => api('/api/wallets/otp-context'),
    refetchInterval: 20_000,
  });
  const ctxByWallet = new Map((otpContext.data ?? []).map((x) => [x.walletId, x]));

  function toggle(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  const agoText = (iso: string | null): string => {
    if (!iso) return '';
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    return s < 60 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
  };

  if (wallets.length === 0) {
    return (
      <p className="py-6 text-sm text-muted-foreground">
        No B2B wallets configured for OTP routing yet. Ask an admin to set a sender pattern on a wallet.
      </p>
    );
  }

  return (
    <form
      className="space-y-4 pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (selectedIds.length === 0) {
          setError('Pick at least one wallet');
          return;
        }
        submit.mutate();
      }}
    >
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        Pick the B2B wallet you're logging into (TBO / Riya / MyPartner /
        Razorpay etc.). Raise the request here, then trigger the OTP on the
        portal — it comes straight to you.
      </div>

      <div className="space-y-2">
        <Label>B2B wallet</Label>
        <div className="flex flex-wrap gap-2">
          {wallets.map((w) => {
            const on = selectedIds.includes(w.id);
            const ctx = ctxByWallet.get(w.id);
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => toggle(w.id)}
                className={
                  'relative rounded-full border px-3 py-1.5 text-sm transition ' +
                  (on
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                    : 'border-border bg-background hover:bg-muted')
                }
              >
                {w.name}
                <span className="ml-1.5 text-xs text-muted-foreground">{w.vendor}</span>
                {ctx ? (
                  <span
                    className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
                    title={`${ctx.unclaimedCount} unclaimed OTP — last ${agoText(ctx.lastOtpAt)}`}
                  >
                    {ctx.unclaimedCount} waiting
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        {selectedIds.some((id) => ctxByWallet.has(id)) ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {selectedIds
              .map((id) => ({ w: wallets.find((x) => x.id === id), ctx: ctxByWallet.get(id) }))
              .filter((x) => x.ctx)
              .map((x) => (
                <div key={x.w?.id}>
                  <span className="font-semibold">{x.w?.name}</span>: {x.ctx!.unclaimedCount} OTP
                  {x.ctx!.unclaimedCount === 1 ? '' : 's'} already waiting (last{' '}
                  {agoText(x.ctx!.lastOtpAt)}). Raising this session delivers it to you.
                </div>
              ))}
          </div>
        ) : null}
      </div>

      {selectedIds.length > 0 ? (
        <div className="space-y-2">
          {selectedIds.map((id) => {
            const w = wallets.find((x) => x.id === id);
            return w ? <WalletPortalLogin key={id} wallet={w} /> : null;
          })}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="wallet-context">Note (optional)</Label>
        <Input
          id="wallet-context"
          placeholder="e.g. Booking #A-2401 hotel reissue"
          value={contextLabel}
          onChange={(e) => setContextLabel(e.target.value)}
          maxLength={120}
        />
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {okMsg ? <p className="text-sm text-emerald-700">{okMsg}</p> : null}

      <Button type="submit" disabled={submit.isPending || selectedIds.length === 0}>
        {submit.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        Request wallet OTP
      </Button>
    </form>
  );
}

// Shared B2B portal login for a wallet — surfaced when the wallet is
// selected in the Raise-OTP flow. Reveal is audited server-side; the
// password is copyable. Admins / accountants can rotate it inline.
function WalletPortalLogin({ wallet }: { wallet: WalletItem }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT' || user?.role === 'OPERATOR';
  const [revealed, setRevealed] = useState<{
    portalUsername: string | null;
    portalPassword: string;
  } | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [u, setU] = useState(wallet.portalUsername ?? '');
  const [p, setP] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ageDays = wallet.portalPasswordUpdatedAt
    ? Math.floor((Date.now() - new Date(wallet.portalPasswordUpdatedAt).getTime()) / 86400000)
    : null;
  const stale = ageDays != null && ageDays >= 14;

  async function reveal() {
    setRevealing(true);
    setErr(null);
    try {
      const r = await api<{ portalUsername: string | null; portalPassword: string }>(
        `/api/wallets/${wallet.id}/reveal-portal`,
        { method: 'POST' },
      );
      setRevealed(r);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Reveal failed');
    }
    setRevealing(false);
  }

  function copyPassword() {
    if (!revealed?.portalPassword) return;
    void navigator.clipboard.writeText(revealed.portalPassword).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function save() {
    if (!u.trim() && !p) {
      setErr('Enter a username and/or password');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/wallets/${wallet.id}/portal-credentials`, {
        method: 'PATCH',
        body: { portalUsername: u.trim() || undefined, portalPassword: p || undefined },
      });
      setEditing(false);
      setP('');
      void qc.invalidateQueries({ queryKey: ['wallets'] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    }
    setBusy(false);
  }

  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2.5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{wallet.name} portal login</span>
        {ageDays != null ? (
          <span
            className={
              'text-[11px] ' +
              (stale ? 'font-semibold text-amber-700' : 'text-muted-foreground')
            }
          >
            {stale ? '⚠ ' : ''}password changed {ageDays === 0 ? 'today' : `${ageDays}d ago`}
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        user: <span className="font-mono text-foreground">{wallet.portalUsername || '—'}</span>
      </div>

      {wallet.hasPortalPassword ? (
        revealed ? (
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">password:</span>
            <span className="font-mono text-sm text-foreground">{revealed.portalPassword}</span>
            <button
              type="button"
              onClick={copyPassword}
              className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] hover:bg-muted"
            >
              {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={reveal}
            disabled={revealing}
            className="mt-1.5 inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
          >
            {revealing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
            Reveal password
          </button>
        )
      ) : (
        <div className="mt-1 text-xs text-muted-foreground">No password saved yet.</div>
      )}

      {canEdit ? (
        editing ? (
          <div className="mt-2 space-y-2 border-t pt-2">
            <Input
              value={u}
              onChange={(e) => setU(e.target.value)}
              placeholder="Portal username"
              className="h-8 text-xs"
            />
            <Input
              value={p}
              onChange={(e) => setP(e.target.value)}
              placeholder="New portal password"
              autoComplete="off"
              className="h-8 text-xs"
            />
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={save} disabled={busy}>
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-1.5 ml-3 text-[11px] text-muted-foreground underline hover:text-foreground"
          >
            {wallet.hasPortalPassword ? 'Update password' : 'Set password'}
          </button>
        )
      ) : null}

      {err ? <p className="mt-1 text-xs text-destructive">{err}</p> : null}
    </div>
  );
}

// Booking-head keys are free strings — the picker (`BookingHeadSelect`) is
// the single source of truth. Kept as `string` here so custom heads added
// by ops (e.g. EMI_PAYMENT) round-trip through form state without cast hoops.
type BookingHead = string;
type PaymentNature = 'CLIENT_BOOKING' | 'OPERATING_EXPENSE';

/** Currencies the form offers. INR is default; the rest cover the
 *  FX-payment cases (Klook, JR SMART EX, MyPartner USD billing, etc.).
 *  Ordered roughly by frequency-of-use for FlyGoldfinch's bookings —
 *  Asia/Europe heavy, then long tail. */
// Legacy — kept only for the "prefill from card_request.currency" check
// below. Replaced in the picker by CURRENCIES from lib/currencies.ts +
// CurrencySelect, which supports typing any 3-letter ISO code.
const SUPPORTED_CURRENCIES = [
  'INR', 'USD', 'EUR', 'GBP', 'JPY', 'SGD', 'AUD', 'AED', 'NZD', 'CHF',
  'ZAR', 'EGP', 'KRW', 'TRY', 'VND',
] as const;
type SupportedCurrency = string;

function CardRequestForm({ cards }: { cards: CardItem[] }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isVisaTeam = user?.team === 'VISA';

  const [cardId, setCardId] = useState('');
  const [paymentNature, setPaymentNature] = useState<PaymentNature>('CLIENT_BOOKING');
  const [bookingHead, setBookingHead] = useState<BookingHead | ''>(isVisaTeam ? 'VISA' : '');
  const [vendor, setVendor] = useState<PickerItem | null>(null);
  const [client, setClient] = useState<PickerItem | null>(null);
  const [trip, setTrip] = useState<PickerItem | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<SupportedCurrency>('INR');
  const [error, setError] = useState<string | null>(null);

  // "Repeat this request" — listen for the global event dispatched by
  // TimelineRow's ↻ button. Pre-fill every field from the prior request
  // so the user only needs to tweak the amount (or not, if it's the
  // same too). Picker components take a {id, label} — we synthesize
  // those from CardRequestItem fields.
  useEffect(() => {
    const onRepeat = (evt: Event) => {
      const detail = (evt as CustomEvent<RepeatRequestDetail>).detail;
      const r = detail?.req;
      if (!r) return;
      if (r.cardId) setCardId(r.cardId);
      const isClient = (r.paymentNature ?? 'CLIENT_BOOKING') === 'CLIENT_BOOKING';
      setPaymentNature(isClient ? 'CLIENT_BOOKING' : 'OPERATING_EXPENSE');
      if (r.bookingHead) setBookingHead(r.bookingHead as BookingHead);
      if (r.vendorId && r.vendor) setVendor({ id: r.vendorId, label: r.vendor });
      else if (r.vendor) setVendor({ id: 'name:' + r.vendor, label: r.vendor });
      if (isClient && r.clientId && r.clientName) setClient({ id: r.clientId, label: r.clientName });
      else if (!isClient) setClient(null);
      if (isClient && r.tripId && r.tripName) setTrip({ id: r.tripId, label: r.tripName });
      else if (!isClient) setTrip(null);
      if (r.amount) setAmount(String(r.amount));
      // Accept any 3-letter ISO code — CurrencySelect handles custom
      // codes now, so we don't need to gate the prefill against the
      // legacy hardcoded list.
      if (r.currency && /^[A-Z]{3}$/.test(r.currency)) {
        setCurrency(r.currency);
      }
      setError(null);
      // Scroll the form into view so the user can immediately see what
      // got pre-filled and tweak / submit.
      requestAnimationFrame(() => {
        document.getElementById('amount')?.focus();
        document.getElementById('amount')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    };
    window.addEventListener(REPEAT_REQUEST_EVENT, onRepeat as EventListener);
    return () => window.removeEventListener(REPEAT_REQUEST_EVENT, onRepeat as EventListener);
  }, []);

  // Booking-head -> vendor-category filter. When the user picks Salary as
  // the booking head, the beneficiary/vendor picker should only surface
  // employees (vendors tagged category=EMPLOYEE) so HR-style payroll runs
  // don't risk picking a flight/hotel vendor by mistake. Applies to BOTH
  // lookup and "create new" — newly added payees on a Salary run are
  // saved as EMPLOYEE by default.
  const isPayroll = bookingHead === 'SALARY';
  const searchVendors = async (q: string): Promise<PickerItem[]> => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', '20');
    if (isPayroll) params.set('category', 'EMPLOYEE');
    const r = await api<{ id: string; name: string; category: string; bankAccountNo: string | null }[]>(
      `/api/vendors?${params.toString()}`,
    );
    return r.map((v) => ({
      id: v.id,
      label: v.name,
      sub: [v.category.toLowerCase(), v.bankAccountNo].filter(Boolean).join(' · '),
    }));
  };
  const createVendor = async (name: string): Promise<PickerItem | null> => {
    const created = await api<{ id: string; name: string }>('/api/vendors', {
      method: 'POST',
      body: { name, category: isPayroll ? 'EMPLOYEE' : 'OTHER' },
    });
    return { id: created.id, label: created.name };
  };

  const searchClients = async (q: string): Promise<PickerItem[]> => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', '20');
    const r = await api<{ id: string; name: string; status: string; contactPhone: string | null }[]>(
      `/api/clients?${params.toString()}`,
    );
    return r.map((c) => ({
      id: c.id,
      label: c.name,
      sub: [c.status.toLowerCase(), c.contactPhone].filter(Boolean).join(' · '),
    }));
  };
  const createClient = async (name: string): Promise<PickerItem | null> => {
    const created = await api<{ id: string; name: string }>('/api/clients', {
      method: 'POST',
      body: { name, status: 'LEAD' },
    });
    return { id: created.id, label: created.name };
  };

  const searchTrips = async (q: string): Promise<PickerItem[]> => {
    if (!client) return [];
    const params = new URLSearchParams();
    params.set('clientId', client.id);
    if (q) params.set('q', q);
    params.set('limit', '20');
    const r = await api<{ id: string; name: string; destination: string | null; status: string }[]>(
      `/api/trips?${params.toString()}`,
    );
    const filtered = q ? r.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())) : r;
    return filtered.map((t) => ({
      id: t.id,
      label: t.name,
      sub: [t.destination, t.status.toLowerCase()].filter(Boolean).join(' · '),
    }));
  };
  const createTrip = async (name: string): Promise<PickerItem | null> => {
    if (!client) return null;
    const created = await api<{ id: string; name: string }>('/api/trips', {
      method: 'POST',
      body: { clientId: client.id, name, status: 'PLANNING' },
    });
    return { id: created.id, label: created.name };
  };

  const isClientBooking = paymentNature === 'CLIENT_BOOKING';

  const [twinNotice, setTwinNotice] = useState<{ requesterName: string; vendor: string; cardLastFour: string | null } | null>(null);
  const submit = useMutation({
    mutationFn: () =>
      api<{ id: string; status?: string; vendor?: string; twin?: { requesterName: string; vendor: string; cardLastFour: string | null } | null }>(
        '/api/card-requests',
        {
          method: 'POST',
          body: {
            cardId,
            vendor: vendor?.label?.trim() ?? '',
            // The picker's id may be a synthesized "name:..." placeholder
            // when prefilled from a request that lacked a real vendorId
            // (free-typed vendor). Only send a real UUID.
            vendorId: vendor?.id && !vendor.id.startsWith('name:') ? vendor.id : undefined,
            clientId: isClientBooking ? client?.id : undefined,
            tripId: isClientBooking ? trip?.id : undefined,
            clientName: isClientBooking ? client?.label : undefined,
            paymentNature,
            bookingHead: bookingHead || undefined,
            amount: bookingHead === 'ADD_BENEFICIARY' ? undefined : (amount || undefined),
            currency: bookingHead === 'ADD_BENEFICIARY' ? undefined : currency,
            isBeneficiaryAddition: bookingHead === 'ADD_BENEFICIARY',
          },
        },
      ),
    onSuccess: (resp) => {
      setVendor(null);
      setClient(null);
      setTrip(null);
      setAmount('');
      setCurrency('INR');
      if (!isVisaTeam) setBookingHead('');
      // The new request lands in the right-panel timeline; its row hosts
      // the 30s countdown + resend button (see TimelineRow).
      qc.invalidateQueries({ queryKey: ['card-requests'] });
      if (resp.twin) setTwinNotice(resp.twin);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? `Could not submit: ${err.status}` : 'Could not submit');
    },
  });

  const ready =
    !!cardId &&
    !!vendor &&
    !!bookingHead &&
    (!isClientBooking || !!client);


  return (
    <form
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (ready) submit.mutate();
      }}
    >
      {twinNotice && (
        <div className="sm:col-span-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/40 dark:bg-amber-900/20 dark:text-amber-200">
          <strong>Twin OTP request.</strong> {twinNotice.requesterName} just raised a request for{' '}
          <span className="font-mono">{twinNotice.vendor}</span>
          {twinNotice.cardLastFour ? ` on card ••${twinNotice.cardLastFour}` : ''}. You'll receive
          both OTPs — match by card last-4 carefully (3 wrong attempts locks the card).
          <button
            type="button"
            onClick={() => setTwinNotice(null)}
            className="ml-2 underline"
          >
            dismiss
          </button>
        </div>
      )}
      {!isVisaTeam && (
        <div className="space-y-2 sm:col-span-2">
          <Label>Payment is for</Label>
          <div className="flex gap-2">
            <NatureChip
              active={paymentNature === 'CLIENT_BOOKING'}
              onClick={() => { setPaymentNature('CLIENT_BOOKING'); setBookingHead(''); }}
            >
              Client booking
            </NatureChip>
            <NatureChip
              active={paymentNature === 'OPERATING_EXPENSE'}
              onClick={() => {
                setPaymentNature('OPERATING_EXPENSE');
                setBookingHead('');
                setClient(null);
                setTrip(null);
              }}
            >
              Office expense
            </NatureChip>
          </div>
        </div>
      )}

      <div className="space-y-2 sm:col-span-2">
        <Label>Card</Label>
        <Picker
          value={
            cardId
              ? (() => {
                  const c = cards.find((x) => x.id === cardId);
                  return c
                    ? { id: c.id, label: `${c.nickname} · ${c.bank} ••${c.lastFour}` }
                    : null;
                })()
              : null
          }
          onChange={(item) => setCardId(item?.id ?? '')}
          search={async (q) => {
            const needle = q.trim().toLowerCase();
            return cards
              .filter((c) => c.active)
              .filter(
                (c) =>
                  !needle ||
                  c.nickname.toLowerCase().includes(needle) ||
                  c.bank.toLowerCase().includes(needle) ||
                  c.lastFour.includes(needle) ||
                  c.cardholderName.toLowerCase().includes(needle),
              )
              .slice(0, 30)
              .map((c) => ({
                id: c.id,
                label: `${c.nickname} · ${c.bank} ••${c.lastFour}`,
                sub: c.cardholderName,
              }));
          }}
          placeholder="Search by last 4, name, or bank — e.g. 7230 / olympus"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="head">Booking head</Label>
        <BookingHeadSelect
          value={bookingHead}
          onChange={(v) => setBookingHead(v as BookingHead)}
          category={isClientBooking ? 'CLIENT_BOOKING' : 'OPERATING_EXPENSE'}
          disabled={isVisaTeam}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
        />
      </div>

      <div className="space-y-2">
        <Label>Vendor / platform</Label>
        <Picker
          value={vendor}
          onChange={setVendor}
          search={searchVendors}
          onCreateNew={createVendor}
          placeholder="MakeMyTrip / Embassy / etc."
        />
      </div>

      {isClientBooking && (
        <>
          <div className="space-y-2">
            <Label>Client / lead</Label>
            <Picker
              value={client}
              onChange={(c) => { setClient(c); setTrip(null); }}
              search={searchClients}
              onCreateNew={createClient}
              placeholder="Search clients — or type a new name"
            />
          </div>
          <div className="space-y-2">
            <Label>Trip</Label>
            <Picker
              value={trip}
              onChange={setTrip}
              search={searchTrips}
              onCreateNew={client ? createTrip : undefined}
              placeholder={client ? 'Pick a trip — or create one' : 'Pick a client first'}
              disabled={!client}
              refetchKey={client?.id}
            />
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="amount">Approx amount</Label>
        <div className="flex gap-2">
          <Input
            id="amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="42000"
            className="flex-1"
          />
          {/* FX support: portals like Klook / JR SMART EX / MyPartner bill
              in the local currency, not INR. Operator picks the actual
              billed currency so the debit matcher knows what to compare
              against (currency-mismatch was the cause of every JPY
              UNATTRIBUTED debit on 2026-05-24). If the currency isn't
              in the ~60-entry curated list, ops can tap "+ Add other
              code…" and type any 3-letter ISO code (SEK/PLN/etc). */}
          <div className="w-32 shrink-0">
            <CurrencySelect
              value={currency}
              onChange={(v) => setCurrency(v)}
            />
          </div>
        </div>
      </div>
      {error && <p className="text-sm text-destructive sm:col-span-2">{error}</p>}
      <div className="sm:col-span-2">
        <Button type="submit" disabled={!ready || submit.isPending}>
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submit.isPending ? 'Submitting…' : 'Submit OTP request'}
        </Button>
      </div>
    </form>
  );
}

function NatureChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-8 items-center rounded-full border px-3 text-xs font-medium transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-input bg-background text-muted-foreground hover:bg-muted'
      }`}
    >
      {children}
    </button>
  );
}

function BankTransferRequestForm({ bankAccounts }: { bankAccounts: BankAccountItem[] }) {
  const qc = useQueryClient();
  const [bankAccountId, setBankAccountId] = useState('');
  const [transferMode, setTransferMode] = useState<'NEFT' | 'IMPS' | 'RTGS' | 'UPI'>('IMPS');
  const [paymentNature, setPaymentNature] = useState<PaymentNature>('CLIENT_BOOKING');
  const [bookingHead, setBookingHead] = useState<BookingHead | ''>('');
  const [vendor, setVendor] = useState<PickerItem | null>(null);
  const [client, setClient] = useState<PickerItem | null>(null);
  const [trip, setTrip] = useState<PickerItem | null>(null);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isClientBooking = paymentNature === 'CLIENT_BOOKING';

  // Booking-head -> vendor-category filter. When the user picks Salary as
  // the booking head, the beneficiary/vendor picker should only surface
  // employees (vendors tagged category=EMPLOYEE) so HR-style payroll runs
  // don't risk picking a flight/hotel vendor by mistake. Applies to BOTH
  // lookup and "create new" — newly added payees on a Salary run are
  // saved as EMPLOYEE by default.
  const isPayroll = bookingHead === 'SALARY';
  const searchVendors = async (q: string): Promise<PickerItem[]> => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', '20');
    if (isPayroll) params.set('category', 'EMPLOYEE');
    const r = await api<{ id: string; name: string; category: string; bankAccountNo: string | null }[]>(
      `/api/vendors?${params.toString()}`,
    );
    return r.map((v) => ({
      id: v.id,
      label: v.name,
      sub: [v.category.toLowerCase(), v.bankAccountNo].filter(Boolean).join(' · '),
    }));
  };
  const createVendor = async (name: string): Promise<PickerItem | null> => {
    const created = await api<{ id: string; name: string }>('/api/vendors', {
      method: 'POST',
      body: { name, category: isPayroll ? 'EMPLOYEE' : 'OTHER' },
    });
    return { id: created.id, label: created.name };
  };

  const searchClients = async (q: string): Promise<PickerItem[]> => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', '20');
    const r = await api<{ id: string; name: string; status: string; contactPhone: string | null }[]>(
      `/api/clients?${params.toString()}`,
    );
    return r.map((c) => ({
      id: c.id,
      label: c.name,
      sub: [c.status.toLowerCase(), c.contactPhone].filter(Boolean).join(' · '),
    }));
  };
  const createClient = async (name: string): Promise<PickerItem | null> => {
    const created = await api<{ id: string; name: string }>('/api/clients', {
      method: 'POST',
      body: { name, status: 'LEAD' },
    });
    return { id: created.id, label: created.name };
  };

  const searchTrips = async (q: string): Promise<PickerItem[]> => {
    if (!client) return [];
    const params = new URLSearchParams();
    params.set('clientId', client.id);
    if (q) params.set('q', q);
    params.set('limit', '20');
    const r = await api<{ id: string; name: string; destination: string | null; status: string }[]>(
      `/api/trips?${params.toString()}`,
    );
    const filtered = q ? r.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())) : r;
    return filtered.map((t) => ({
      id: t.id,
      label: t.name,
      sub: [t.destination, t.status.toLowerCase()].filter(Boolean).join(' · '),
    }));
  };
  const createTrip = async (name: string): Promise<PickerItem | null> => {
    if (!client) return null;
    const created = await api<{ id: string; name: string }>('/api/trips', {
      method: 'POST',
      body: { clientId: client.id, name, status: 'PLANNING' },
    });
    return { id: created.id, label: created.name };
  };

  const submit = useMutation({
    mutationFn: () =>
      api('/api/bank-transfer-requests', {
        method: 'POST',
        body: {
          bankAccountId,
          transferMode,
          beneficiaryName: vendor?.label ?? '',
          vendorId: vendor?.id ? vendor.id : undefined,
          clientId: isClientBooking ? client?.id : undefined,
          tripId: isClientBooking ? trip?.id : undefined,
          clientName: isClientBooking ? client?.label : undefined,
          paymentNature,
          bookingHead: bookingHead || undefined,
          // ADD_BENEFICIARY OTPs are pre-add registration with the bank
          // — there's no money moving yet, so amount is a nominal 0 and
          // never shown in the form.
          amount: isAddBene ? '0' : amount,
          // isNewBeneficiary is now driven purely by the ADD_BENEFICIARY
          // booking head. The standalone checkbox + its hold-window
          // logic were removed — banks enforce their own post-add
          // cooldown, so admins pass beneficiary-add OTPs immediately.
          isNewBeneficiary: isAddBene,
        },
      }),
    onSuccess: () => {
      setVendor(null);
      setClient(null);
      setTrip(null);
      setAmount('');
      setBookingHead('');
      qc.invalidateQueries({ queryKey: ['bank-transfer-requests'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? `Could not submit: ${err.status}` : 'Could not submit');
    },
  });

  const isAddBene = bookingHead === 'ADD_BENEFICIARY';
  const ready =
    !!bankAccountId &&
    !!vendor &&
    (isAddBene || !!amount) &&
    !!bookingHead &&
    (!isClientBooking || !!client);

  return (
    <form
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (ready) submit.mutate();
      }}
    >
      <div className="space-y-2 sm:col-span-2">
        <Label>Payment is for</Label>
        <div className="flex gap-2">
          <NatureChip
            active={paymentNature === 'CLIENT_BOOKING'}
            onClick={() => { setPaymentNature('CLIENT_BOOKING'); setBookingHead(''); }}
          >
            Client booking
          </NatureChip>
          <NatureChip
            active={paymentNature === 'OPERATING_EXPENSE'}
            onClick={() => {
              setPaymentNature('OPERATING_EXPENSE');
              setBookingHead('');
              setClient(null);
              setTrip(null);
            }}
          >
            Office expense
          </NatureChip>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="bacc-pick">From bank account</Label>
        <select
          id="bacc-pick"
          required
          value={bankAccountId}
          onChange={(e) => setBankAccountId(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">— Select bank account —</option>
          {bankAccounts
            .filter((b) => b.active)
            .map((b) => (
              <option key={b.id} value={b.id}>
                {b.nickname} · {b.bank}{b.accountNumberMasked ? ` (${b.accountNumberMasked})` : ''}
              </option>
            ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="mode">Transfer mode</Label>
        <select
          id="mode"
          value={transferMode}
          onChange={(e) => setTransferMode(e.target.value as typeof transferMode)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="IMPS">IMPS — instant</option>
          <option value="NEFT">NEFT — batched</option>
          <option value="RTGS">RTGS — large (₹2L+)</option>
          <option value="UPI">UPI</option>
        </select>
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="bhead">Booking head</Label>
        <BookingHeadSelect
          value={bookingHead}
          onChange={(v) => setBookingHead(v as BookingHead)}
          category={isClientBooking ? 'CLIENT_BOOKING' : 'OPERATING_EXPENSE'}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label>Beneficiary / vendor</Label>
        {isAddBene ? (
          // Pure free-text when registering a NEW bene at the bank.
          // No suggestions, no autocomplete from existing vendors —
          // the whole point is the name doesn't exist yet.
          <Input
            autoFocus
            value={vendor?.label ?? ''}
            onChange={(e) =>
              setVendor(e.target.value ? { id: '', label: e.target.value } : null)
            }
            placeholder="Type the new beneficiary's name"
          />
        ) : (
          <Picker
            value={vendor}
            onChange={setVendor}
            search={searchVendors}
            onCreateNew={createVendor}
            placeholder="Search vendors — or type a new name"
          />
        )}
        <p className="text-[11px] text-muted-foreground">
          {isAddBene
            ? 'Fresh name only — this OTP is just for the bank-side bene-add step.'
            : 'Account number + IFSC are already saved in netbanking — no need to repeat them here.'}
        </p>
      </div>
      {isClientBooking && (
        <>
          <div className="space-y-2">
            <Label>Client / lead</Label>
            <Picker
              value={client}
              onChange={(c) => { setClient(c); setTrip(null); }}
              search={searchClients}
              onCreateNew={createClient}
              placeholder="Search clients — or type a new name"
            />
          </div>
          <div className="space-y-2">
            <Label>Trip</Label>
            <Picker
              value={trip}
              onChange={setTrip}
              search={searchTrips}
              onCreateNew={client ? createTrip : undefined}
              placeholder={client ? 'Pick a trip — or create one' : 'Pick a client first'}
              disabled={!client}
              refetchKey={client?.id}
            />
          </div>
        </>
      )}
      {!isAddBene && (
        <div className="space-y-2">
          <Label htmlFor="bamt">Amount (INR)</Label>
          <Input
            id="bamt"
            required
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="9760.00"
          />
        </div>
      )}
      {isAddBene && (
        <p className="self-end pb-2 text-xs text-muted-foreground sm:col-span-2">
          Adding a fresh beneficiary — no amount needed. The OTP is just for the bank-side bene-add step; raise a separate transfer request once you're ready to send money.
        </p>
      )}
      {error && <p className="text-sm text-destructive sm:col-span-2">{error}</p>}
      <div className="sm:col-span-2">
        <Button type="submit" disabled={!ready || submit.isPending}>
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submit.isPending ? 'Submitting…' : 'Submit OTP request'}
        </Button>
      </div>
    </form>
  );
}

function RequestsSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
      ))}
    </div>
  );
}

/** Right-panel timeline of the current user's last 5 raised card_requests.
 *  Each row shows the lifecycle: pending → approved → OTP received. When
 *  the engine delivers an OTP bound to a request id, the row inflates
 *  with the live OTP code + SMS body so the user reads it inline
 *  instead of having to hunt through the OTP log. */
type TimelineEntry =
  | { kind: 'card'; key: string; ts: number; req: CardRequestItem }
  | { kind: 'wallet'; key: string; ts: number; s: WalletSessionItem }
  | { kind: 'bank'; key: string; ts: number; req: BankTransferRequestItem };

const TIMELINE_VISIBLE = 6;

function RequestTimeline({
  requests,
  bankRequests,
  walletSessions,
  otpsByRequest,
}: {
  requests: CardRequestItem[];
  bankRequests: BankTransferRequestItem[];
  walletSessions: WalletSessionItem[];
  otpsByRequest: Record<string, { otpCode: string; smsBody: string; senderId: string; receivedAt: string }>;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);

  // One recency-sorted feed merging card + bank-transfer requests +
  // wallet sessions. The sort timestamp is the most meaningful moment
  // for each: OTP delivery if it happened, else approval, else raise.
  const entries: TimelineEntry[] = [
    ...requests.map((r): TimelineEntry => ({
      kind: 'card',
      key: `c:${r.id}`,
      req: r,
      ts: new Date(otpsByRequest[r.id]?.receivedAt ?? r.approvedAt ?? r.createdAt).getTime(),
    })),
    ...bankRequests.map((r): TimelineEntry => ({
      kind: 'bank',
      key: `b:${r.id}`,
      req: r,
      ts: new Date(r.otpReceivedAt ?? r.approvedAt ?? r.createdAt).getTime(),
    })),
    ...walletSessions.map((s): TimelineEntry => ({
      kind: 'wallet',
      key: `w:${s.id}`,
      s,
      ts: new Date(s.otpDeliveredAt ?? s.startedAt).getTime(),
    })),
  ].sort((a, b) => b.ts - a.ts);

  const visible = entries.slice(0, TIMELINE_VISIBLE);
  const overflow = entries.length - TIMELINE_VISIBLE;

  const renderEntry = (e: TimelineEntry) => {
    if (e.kind === 'wallet') return <WalletSessionRow key={e.key} s={e.s} />;
    if (e.kind === 'bank') return <BankTransferRow key={e.key} req={e.req} />;
    return <TimelineRow key={e.key} req={e.req} otp={otpsByRequest[e.req.id]} />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Your recent OTP requests</CardTitle>
        <CardDescription>Live status from raise → approval → OTP delivery.</CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing yet. Raise an OTP request and you'll see it here.
          </p>
        ) : (
          <>
            <ul className="space-y-3">{visible.map(renderEntry)}</ul>
            {overflow > 0 ? (
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 py-2 text-xs font-semibold text-zinc-600 hover:bg-zinc-100"
              >
                <span className="text-base font-bold leading-none tracking-widest">···</span>
                Show all {entries.length} requests
              </button>
            ) : null}
          </>
        )}
      </CardContent>
      {historyOpen ? (
        <TimelineHistoryModal onClose={() => setHistoryOpen(false)}>
          <ul className="space-y-3">{entries.map(renderEntry)}</ul>
        </TimelineHistoryModal>
      ) : null}
    </Card>
  );
}

/** Full-history popup for the recent-OTP pane — opened by the "···"
 *  button once there are more than TIMELINE_VISIBLE requests. */
function TimelineHistoryModal({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16"
      onMouseDown={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <h2 className="text-sm font-bold text-zinc-900">All OTP requests</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-0.5 text-lg leading-none text-zinc-500 hover:bg-zinc-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

/** Timeline row for a bank-transfer OTP request. Tracks raise → approval
 *  → completion, and (when the OTP has been delivered) taps open to show
 *  the code + message — same collapse behaviour as the other rows. */
function BankTransferRow({ req }: { req: BankTransferRequestItem }) {
  const [collapsed, setCollapsed] = useState(true);
  const otpCode = req.otpSmsBody ? (req.otpSmsBody.match(/\b\d{4,8}\b/) ?? [''])[0]! : '';
  const canCollapse = !!req.otpSmsBody;
  // An arrived OTP trumps the backend status.
  const effectiveStatus = req.otpSmsBody ? 'OTP_RECEIVED' : req.status;
  const tone = statusTone(effectiveStatus);
  // Bank-transfer-specific label override. The engine flips `status` to
  // COMPLETED the moment it dispatches the OTP to the requester — *not*
  // when the actual transfer settles (the debit SMS does that later, in
  // a different code path). So a bare "COMPLETED" badge misleads the
  // user into thinking the money moved when really the OTP just got
  // routed. Show "OTP DELIVERED" until the matching debit confirms.
  const displayLabel = req.status === 'COMPLETED' && !req.otpSmsBody
    ? 'OTP DELIVERED'
    : effectiveStatus === 'OTP_RECEIVED'
      ? 'OTP DELIVERED'
      : tone.label;
  const amountLabel = req.amount ? `₹${Number(req.amount).toLocaleString('en-IN')}` : '—';

  // Re-request + withdraw, mirroring the card-OTP row. Live only while the
  // request is in flight (PENDING / APPROVED and no OTP yet).
  const isAwaiting = !req.otpSmsBody && (req.status === 'PENDING' || req.status === 'APPROVED');
  const qc = useQueryClient();
  const [nudgedAt, setNudgedAt] = useState<number | null>(null);
  const anchorMs = nudgedAt ?? new Date(req.createdAt).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isAwaiting) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isAwaiting]);
  const secsLeft = Math.max(0, 30 - Math.floor((now - anchorMs) / 1000));

  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const nudge = async () => {
    setBusy(true);
    setNote(null);
    try {
      await api(`/api/bank-transfer-requests/${req.id}/nudge`, { method: 'POST' });
      setNote(req.status === 'PENDING' ? 'Admin pinged again.' : 'Relay phone re-checked.');
      setNudgedAt(Date.now());
    } catch (e) {
      const err = e as ApiError;
      setNote(err.status === 429 ? 'Hold on — wait a few seconds.' : 'Could not resend.');
    } finally {
      setBusy(false);
    }
  };

  // Withdraw a request raised by mistake — PENDING-only, two-tap confirm.
  const [withdrawing, setWithdrawing] = useState(false);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);
  const withdraw = async () => {
    setWithdrawing(true);
    setNote(null);
    try {
      await api(`/api/bank-transfer-requests/${req.id}/cancel`, { method: 'POST' });
      setNote('Request withdrawn.');
      setConfirmingWithdraw(false);
      void qc.invalidateQueries({ queryKey: ['bank-transfer-requests'] });
    } catch (e) {
      const err = e as ApiError;
      setNote(
        err.status === 409 ? 'Too late — this request was already actioned.' : 'Could not withdraw.',
      );
      setConfirmingWithdraw(false);
    } finally {
      setWithdrawing(false);
    }
  };

  const subParts: string[] = [];
  if (req.clientName) subParts.push(req.clientName);
  if (canCollapse) subParts.push(collapsed ? 'tap to view OTP' : 'tap to hide');
  return (
    <li
      className={`rounded-xl border ${tone.border} ${tone.bg} px-3.5 py-3 ${
        canCollapse ? 'cursor-pointer' : ''
      }`}
      onClick={canCollapse ? () => setCollapsed((c) => !c) : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-foreground">
            Bank transfer for {amountLabel}
            <span className="text-muted-foreground"> to </span>
            {req.beneficiaryName}
          </p>
          {subParts.length > 0 ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{subParts.join(' · ')}</p>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone.chip}`}
        >
          {displayLabel}
        </span>
      </div>
      {req.otpSmsBody && !collapsed ? (
        <div className="mt-2.5 rounded-lg bg-white/60 px-3 py-2 ring-1 ring-emerald-200">
          <div className="flex items-center gap-2">
            <span className="font-mono text-base font-bold tracking-[0.2em] text-emerald-900">
              {otpCode}
            </span>
            <span className="ml-auto text-[10px] text-emerald-700">OTP received</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
            {req.otpSmsBody}
          </p>
        </div>
      ) : null}
      {isAwaiting ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={nudge}
            disabled={secsLeft > 0 || busy || withdrawing}
            className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            {busy
              ? 'Resending…'
              : secsLeft > 0
                ? `Resend in ${secsLeft}s`
                : req.status === 'PENDING'
                  ? 'Resend approval request'
                  : 'Re-check phone'}
          </button>
          {req.status === 'PENDING' ? (
            confirmingWithdraw ? (
              <>
                <button
                  type="button"
                  onClick={withdraw}
                  disabled={withdrawing}
                  className="rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  {withdrawing ? 'Withdrawing…' : 'Confirm withdraw'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingWithdraw(false)}
                  disabled={withdrawing}
                  className="rounded-md px-1.5 py-1 text-[11px] font-semibold text-zinc-500 hover:text-zinc-700 disabled:opacity-50"
                >
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingWithdraw(true)}
                disabled={busy}
                className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                Withdraw request
              </button>
            )
          ) : null}
          {note ? <span className="text-[10px] text-muted-foreground">{note}</span> : null}
        </div>
      ) : null}
      <p className="mt-2 text-[10px] tabular-nums text-muted-foreground">
        {formatTimeAgo(req.otpReceivedAt ?? req.approvedAt ?? req.createdAt)}
      </p>
    </li>
  );
}

/** Timeline row for a wallet OTP request — a session row, not a
 *  card_request. Status comes from the session's live state + otpCount. */
function WalletSessionRow({ s }: { s: WalletSessionItem }) {
  const [flipped, setFlipped] = useState(false);
  const live = new Date(s.expiresAt).getTime() > Date.now() && s.status === 'ACTIVE';
  const delivered = !!s.otpDeliveredAt || s.status === 'ENDED_EARLY';
  const [tone, label] = delivered
    ? ['border-emerald-200 bg-emerald-50/60', 'OTP delivered']
    : live
      ? ['border-blue-200 bg-blue-50/60', 'armed']
      : ['border-zinc-200 bg-zinc-50', 'expired'];
  const agoSec = Math.max(0, Math.round((Date.now() - new Date(s.startedAt).getTime()) / 1000));
  const ago = agoSec < 60 ? `${agoSec}s ago` : `${Math.round(agoSec / 60)}m ago`;
  const otpCode = s.otpSmsBody ? (s.otpSmsBody.match(/\b\d{4,8}\b/) ?? [''])[0]! : '';
  // A delivered OTP flips to reveal the code + full message — kept for
  // audit (who got which OTP, exact text, when).
  const canFlip = delivered && !!s.otpSmsBody;
  return (
    <li
      className={`rounded-xl border px-3.5 py-3 ${tone} ${canFlip ? 'cursor-pointer' : ''}`}
      onClick={canFlip ? () => setFlipped((f) => !f) : undefined}
    >
      {flipped && canFlip ? (
        <div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
              Delivered OTP · {s.walletName ?? 'wallet'}
            </p>
            <span className="text-[10px] text-muted-foreground">tap to flip back</span>
          </div>
          {otpCode ? (
            <p className="mt-1 font-mono text-2xl font-bold tracking-[0.2em] text-foreground">
              {otpCode}
            </p>
          ) : null}
          <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-snug text-muted-foreground">
            {s.otpSmsBody}
          </p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            {s.otpDeliveredAt ? `delivered ${new Date(s.otpDeliveredAt).toLocaleString()}` : ''}
            {s.otpSenderId ? ` · ${s.otpSenderId}` : ''}
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug text-foreground">
              Wallet OTP
              <span className="text-muted-foreground"> · </span>
              {s.walletName ?? 'wallet'}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {delivered ? `delivered · raised ${ago}` : `B2B portal login · armed ${ago}`}
              {canFlip ? ' · tap to view OTP' : ''}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              delivered
                ? 'border-emerald-300 bg-emerald-100 text-emerald-800'
                : live
                  ? 'border-blue-300 bg-blue-100 text-blue-800'
                  : 'border-zinc-300 bg-zinc-100 text-zinc-600'
            }`}
          >
            {label}
          </span>
        </div>
      )}
    </li>
  );
}

function TimelineRow({
  req,
  otp,
}: {
  req: CardRequestItem;
  otp?: { otpCode: string; smsBody: string; senderId: string; receivedAt: string };
}) {
  // OTP source: a live SSE event (otpsByRequest) if one landed this
  // session, else the server-joined OTP — which survives a page reload.
  const serverOtp = req.otpSmsBody
    ? {
        otpCode: (req.otpSmsBody.match(/\b\d{4,8}\b/) ?? [''])[0]!,
        smsBody: req.otpSmsBody,
        senderId: req.otpSenderId ?? '',
        receivedAt: req.otpReceivedAt ?? req.createdAt,
      }
    : undefined;
  const effectiveOtp = otp ?? serverOtp;

  // Status precedence: an arrived OTP trumps any backend status because
  // the row should celebrate the delivery the moment it lands.
  const effectiveStatus = effectiveOtp ? 'OTP_RECEIVED' : req.status;
  const tone = statusTone(effectiveStatus);
  const amountLabel = req.amount
    ? (req.currency && req.currency !== 'INR'
        ? `${req.currency} ${Number(req.amount).toLocaleString('en-IN')}`
        : `₹${Number(req.amount).toLocaleString('en-IN')}`)
    : null;
  const owner = req.cardOwnerName ?? 'card owner';
  const card = req.cardLabel ?? 'card';

  // "Same again" handler (button visibility computed after isAwaiting
  // is set below — referenced via the `showRepeat` const).
  const onRepeat = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent<RepeatRequestDetail>(REPEAT_REQUEST_EVENT, { detail: { req } }));
  };

  // The row gets a re-request button once the OTP hasn't shown up. It's
  // live only while the request is still in flight (PENDING / APPROVED
  // and no OTP yet) — terminal states (completed/declined/timed-out) or
  // a delivered OTP hide it.
  const isAwaiting = !effectiveOtp && (req.status === 'PENDING' || req.status === 'APPROVED');
  // Show the ↻ Same again button on any row that isn't still in-flight.
  const showRepeat = !isAwaiting;

  // Card OTP detail collapses like the wallet rows — tap the row to
  // show / hide the delivered code. Starts collapsed, for parity.
  const [collapsed, setCollapsed] = useState(true);
  const canCollapse = !!effectiveOtp;

  // 30s cooldown anchored to the last action. `nudgedAt` resets it.
  const [nudgedAt, setNudgedAt] = useState<number | null>(null);
  const anchorMs = nudgedAt ?? new Date(req.createdAt).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isAwaiting) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isAwaiting]);
  const secsLeft = Math.max(0, 30 - Math.floor((now - anchorMs) / 1000));

  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const nudge = async () => {
    setBusy(true);
    setNote(null);
    try {
      await api(`/api/card-requests/${req.id}/nudge`, { method: 'POST' });
      setNote(req.status === 'PENDING' ? 'Admin pinged again.' : 'Relay phone re-checked.');
      setNudgedAt(Date.now());
    } catch (e) {
      const err = e as ApiError;
      setNote(err.status === 429 ? 'Hold on — wait a few seconds.' : 'Could not resend.');
    } finally {
      setBusy(false);
    }
  };

  // Withdraw a request raised by mistake. Only valid while PENDING — the
  // backend rejects a cancel once an admin has approved. Two-tap: the first
  // click arms the confirm so a fumbled tap can't drop a live request.
  const [withdrawing, setWithdrawing] = useState(false);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);
  const withdraw = async () => {
    setWithdrawing(true);
    setNote(null);
    try {
      await api(`/api/card-requests/${req.id}/cancel`, { method: 'POST' });
      setNote('Request withdrawn.');
      setConfirmingWithdraw(false);
      void qc.invalidateQueries({ queryKey: ['card-requests'] });
    } catch (e) {
      const err = e as ApiError;
      setNote(
        err.status === 409 ? 'Too late — this request was already actioned.' : 'Could not withdraw.',
      );
      setConfirmingWithdraw(false);
    } finally {
      setWithdrawing(false);
    }
  };

  const subParts: string[] = [];
  if (req.vendor) {
    subParts.push(req.vendor);
    if (req.clientName) subParts.push(req.clientName);
  }
  if (canCollapse) subParts.push(collapsed ? 'tap to view OTP' : 'tap to hide');

  return (
    <li
      className={`rounded-xl border ${tone.border} ${tone.bg} px-3.5 py-3 transition-all ${
        canCollapse ? 'cursor-pointer' : ''
      }`}
      onClick={canCollapse ? () => setCollapsed((c) => !c) : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-foreground">
            OTP request for {amountLabel ?? '—'}
            <span className="text-muted-foreground"> raised to </span>
            {owner}
            <span className="text-muted-foreground"> for </span>
            {card}
          </p>
          {subParts.length > 0 ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{subParts.join(' · ')}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone.chip}`}
          >
            {tone.label}
          </span>
          {showRepeat ? (
            <button
              type="button"
              onClick={onRepeat}
              title="Pre-fill the form with this request's details so you can raise the same one again with just a new amount"
              className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-[10px] font-medium text-zinc-700 hover:bg-zinc-50"
            >
              ↻ Same again
            </button>
          ) : null}
        </div>
      </div>
      {effectiveOtp && !collapsed ? (
        <div className="mt-2.5 rounded-lg bg-white/60 px-3 py-2 ring-1 ring-emerald-200">
          <div className="flex items-center gap-2">
            <span className="font-mono text-base font-bold tracking-[0.2em] text-emerald-900">
              {effectiveOtp.otpCode}
            </span>
            <span className="ml-auto text-[10px] text-emerald-700">OTP received</span>
          </div>
          {effectiveOtp.smsBody ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
              {effectiveOtp.smsBody}
            </p>
          ) : null}
        </div>
      ) : null}
      {isAwaiting ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={nudge}
            disabled={secsLeft > 0 || busy || withdrawing}
            className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            {busy
              ? 'Resending…'
              : secsLeft > 0
                ? `Resend in ${secsLeft}s`
                : req.status === 'PENDING'
                  ? 'Resend approval request'
                  : 'Re-check phone'}
          </button>
          {req.status === 'PENDING' ? (
            confirmingWithdraw ? (
              <>
                <button
                  type="button"
                  onClick={withdraw}
                  disabled={withdrawing}
                  className="rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  {withdrawing ? 'Withdrawing…' : 'Confirm withdraw'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingWithdraw(false)}
                  disabled={withdrawing}
                  className="rounded-md px-1.5 py-1 text-[11px] font-semibold text-zinc-500 hover:text-zinc-700 disabled:opacity-50"
                >
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingWithdraw(true)}
                disabled={busy}
                className="rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                Withdraw request
              </button>
            )
          ) : null}
          {note ? <span className="text-[10px] text-muted-foreground">{note}</span> : null}
        </div>
      ) : null}
      <p className="mt-2 text-[10px] tabular-nums text-muted-foreground">
        {formatTimeAgo(effectiveOtp?.receivedAt ?? req.approvedAt ?? req.createdAt)}
      </p>
    </li>
  );
}

function statusTone(status: string): { label: string; chip: string; bg: string; border: string } {
  switch (status) {
    case 'OTP_RECEIVED':
      return {
        label: 'OTP received',
        chip: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        bg: 'bg-emerald-50/40',
        border: 'border-emerald-200',
      };
    case 'COMPLETED':
      return {
        label: 'completed',
        chip: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        bg: 'bg-white',
        border: 'border-zinc-200',
      };
    case 'APPROVED':
      return {
        label: 'approved · awaiting OTP',
        chip: 'bg-blue-50 text-blue-800 border-blue-200',
        bg: 'bg-white',
        border: 'border-blue-200',
      };
    case 'PENDING':
      return {
        label: 'pending',
        chip: 'bg-amber-50 text-amber-900 border-amber-200',
        bg: 'bg-white',
        border: 'border-amber-200',
      };
    case 'DECLINED':
      return {
        label: 'declined',
        chip: 'bg-rose-50 text-rose-800 border-rose-200',
        bg: 'bg-white',
        border: 'border-zinc-200',
      };
    case 'TIMED_OUT':
      return {
        label: 'timed out',
        chip: 'bg-zinc-100 text-zinc-700 border-zinc-200',
        bg: 'bg-white',
        border: 'border-zinc-200',
      };
    default:
      return {
        label: status.toLowerCase(),
        chip: 'bg-zinc-100 text-zinc-700 border-zinc-200',
        bg: 'bg-white',
        border: 'border-zinc-200',
      };
  }
}

